import React, { useState } from 'react';
import { Download, X, Copy, Check } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import walletImage from '../assets/img/wallet1.png';
import walletImageDark from '../assets/img/wallet1_dark.png';

const Wallet: React.FC = () => {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const currentImage = theme === 'dark' ? walletImageDark : walletImage;
  const [showMobileAlert, setShowMobileAlert] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const handleDownloadClick = () => {
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      setShowMobileAlert(true);
    } else {
      window.open('https://chromewebstore.google.com/detail/h2-wallet/efoempndpndipldoiefpinclpgfbbbek?authuser=0&hl=zh-CN', '_blank');
    }
  };

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#111111] pt-16 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12 sm:py-16 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <div className="space-y-6 sm:space-y-8">
            {/* Title */}
            <h1 className="text-2xl sm:text-3xl lg:text-4xl text-gray-900 dark:text-gray-100">
              {t('wallet.title')}
            </h1>

            {/* Description */}
            <p className="text-base sm:text-lg text-gray-700 dark:text-gray-300">
              {t('wallet.description')}
            </p>

            {/* Features with green checkmarks */}
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-[#86E92A] flex items-center justify-center">
                  <svg className="w-3 h-3 sm:w-4 sm:h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-sm sm:text-base text-gray-900 dark:text-gray-100 font-medium">
                  {t('wallet.feature1')}
                </span>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-[#86E92A] flex items-center justify-center">
                  <svg className="w-3 h-3 sm:w-4 sm:h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-sm sm:text-base text-gray-900 dark:text-gray-100 font-medium">
                  {t('wallet.feature2')}
                </span>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-[#86E92A] flex items-center justify-center">
                  <svg className="w-3 h-3 sm:w-4 sm:h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-sm sm:text-base text-gray-900 dark:text-gray-100 font-medium">
                  {t('wallet.feature3')}
                </span>
              </div>
            </div>

            {/* What's New Section */}
            <div className="space-y-3 sm:space-y-4 pt-4">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('wallet.whatsNew')}
              </h2>
              <div className="space-y-2">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-2 h-2 rounded-full bg-[#86E92A] mt-2"></div>
                  <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300">
                    {t('wallet.news1')}
                  </span>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-2 h-2 rounded-full bg-[#86E92A] mt-2"></div>
                  <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300">
                    {t('wallet.news2')}
                  </span>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-2 h-2 rounded-full bg-[#86E92A] mt-2"></div>
                  <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300">
                    {t('wallet.news3')}
                  </span>
                </div>
              </div>
            </div>
            {/* Download Button */}
            <button onClick={handleDownloadClick} className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 bg-black dark:bg-white text-white dark:text-black text-sm sm:text-base font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors duration-200 mt-4">
              {t('wallet.download')}
              {/* <Download className="w-4 h-4 sm:w-5 sm:h-5" /> */}
            </button>
            {/* Network Configuration Guide */}
            <div className="space-y-3 sm:space-y-4 pt-4">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('wallet.networkGuideTitle')}
              </h2>
              <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300">
                {t('wallet.networkGuideDesc')}
              </p>
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 space-y-3 border border-gray-200 dark:border-gray-700">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 min-w-[140px]">
                    {t('wallet.rpcUrl')}:
                  </span>
                  <div className="flex items-center gap-2 flex-1">
                    <code className="text-sm text-[#86E92A] bg-gray-900/5 dark:bg-gray-900/50 px-2 py-1 rounded break-all flex-1">
                      https://rpc.h2chain.io
                    </code>
                    <button
                      onClick={() => handleCopy('https://rpc.h2chain.io', 'rpc')}
                      className="flex-shrink-0 p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                      title="Copy"
                    >
                      {copiedField === 'rpc' ? (
                        <Check className="w-4 h-4 text-[#86E92A]" />
                      ) : (
                        <Copy className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 min-w-[140px]">
                    {t('wallet.chainId')}:
                  </span>
                  <div className="flex items-center gap-2 flex-1">
                    <code className="text-sm text-[#86E92A] bg-gray-900/5 dark:bg-gray-900/50 px-2 py-1 rounded flex-1">
                      2582
                    </code>
                    <button
                      onClick={() => handleCopy('2582', 'chainId')}
                      className="flex-shrink-0 p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                      title="Copy"
                    >
                      {copiedField === 'chainId' ? (
                        <Check className="w-4 h-4 text-[#86E92A]" />
                      ) : (
                        <Copy className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 min-w-[140px]">
                    {t('wallet.explorer')}:
                  </span>
                  <div className="flex items-center gap-2 flex-1">
                    <code className="text-sm text-[#86E92A] bg-gray-900/5 dark:bg-gray-900/50 px-2 py-1 rounded break-all flex-1">
                      https://h2scan.io
                    </code>
                    <button
                      onClick={() => handleCopy('https://h2scan.io', 'explorer')}
                      className="flex-shrink-0 p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                      title="Copy"
                    >
                      {copiedField === 'explorer' ? (
                        <Check className="w-4 h-4 text-[#86E92A]" />
                      ) : (
                        <Copy className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          
         
          </div>

          {/* Right Image */}
          <div className="relative order-first lg:order-last">
            <img
              src={currentImage}
              alt="H2 Wallet Interface"
              className="w-full h-auto object-contain transform scale-110 sm:scale-[1.2] lg:scale-[1.3] lg:translate-x-8 lg:-translate-y-12"
            />
          </div>
        </div>
      </div>

      {/* Mobile Alert Modal */}
      {showMobileAlert && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-sm w-full p-6 relative">
            <button
              onClick={() => setShowMobileAlert(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="text-center">
              <div className="mb-4">
                <Download className="w-12 h-12 mx-auto text-[#86E92A]" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {t('wallet.mobileAlertTitle')}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
                {t('wallet.mobileAlertMessage')}
              </p>
              <button
                onClick={() => setShowMobileAlert(false)}
                className="w-full px-4 py-2 bg-[#86E92A] text-black font-medium rounded-lg hover:bg-[#75d320] transition-colors duration-200"
              >
                {t('wallet.understood')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Wallet;
